<template>
  <div class="min-h-screen bg-[#F8FAFC] flex flex-col font-sans text-slate-900 selection:bg-blue-100">
    
    <!-- EN-TÊTE PRINCIPAL -->
    <header class="bg-white border-b border-slate-200 px-6 lg:px-8 py-4 flex  md:flex-row justify-between items-start md:items-center gap-4 z-20 shadow-sm">
      <div class="flex items-center gap-3.5">
        <div class="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-md shadow-blue-500/20">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        </div>
        <div>
          <div class="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <span>Medialink Solutions</span>
            <span>/</span>
            <span class="text-blue-600">Médiathèque</span>
          </div>
          <h1 class="text-xl font-bold tracking-tight text-slate-900">Gestionnaire d'Assets & Médias</h1>
        </div>
      </div>

      <div class="flex items-center gap-3 w-full md:w-auto justify-end">
        <button 
          @click="fetchMedia(); fetchFolders();" 
          class="p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-blue-600 hover:border-blue-200 transition-all"
          title="Actualiser"
        >
          <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        </button>
        <button 
          @click="showUploadModal = true"
          class="flex items-center justify-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:bg-blue-700 shadow-sm transition-all"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M12 4v16m8-8H4" /></svg>
          Uploader des Médias
        </button>
      </div>
    </header>

    <div class="flex flex-1 overflow-hidden">
      
      <!-- SIDEBAR DES DOSSIERS -->
      <aside class="w-64 bg-white border-r border-slate-200 hidden lg:flex flex-col p-5 overflow-y-auto">
        <div class="mb-6">
          <h3 class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Dossiers Officines</h3>
          <nav class="space-y-1">
            <button
              @click="selectFolder('all')"
              :class="selectedFolder === 'all' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-slate-600 hover:bg-slate-50'"
              class="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
            >
              <div class="flex items-center gap-2.5">
                <svg class="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                <span>Tous les fichiers</span>
              </div>
              <span class="text-[10px] font-bold opacity-65 tabular-nums">{{ totalCount }}</span>
            </button>

            <button
              v-for="folder in folders"
              :key="folder.name"
              @click="selectFolder(folder.name)"
              :class="selectedFolder === folder.name ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-slate-600 hover:bg-slate-50'"
              class="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
            >
              <div class="flex items-center gap-2.5 truncate">
                <svg class="w-4 h-4 opacity-70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                <span class="truncate">{{ folder.name }}</span>
              </div>
              <span class="text-[10px] font-bold opacity-65 tabular-nums shrink-0">{{ folder.count }}</span>
            </button>
          </nav>
        </div>

        <!-- Jauge Stockage Cloud -->
        <div class="mt-auto pt-4 border-t border-slate-100">
           <div class="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Stockage Cloud</p>
              <div class="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden mb-1.5">
                <div class="h-full bg-blue-600 w-[65%]"></div>
              </div>
              <p class="text-[10px] font-semibold text-slate-500 text-right">6.5 GB / 10 GB</p>
           </div>
        </div>
      </aside>

      <!-- ZONE CENTRALE DES MÉDIAS -->
      <main class="flex-1 flex flex-col min-w-0 bg-[#F8FAFC]">
        
        <!-- Barre de recherche et filtres -->
        <div class="px-6 py-4 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
          <div class="flex-1 min-w-[280px] relative">
            <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </span>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Filtrer par nom, tag ou format..." 
              class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 text-xs font-medium text-slate-700"
              @input="debounceSearch"
            >
          </div>

          <div class="flex items-center gap-3">
            <div class="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button @click="viewMode = 'grid'" :class="viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400'" class="p-1.5 rounded-lg transition-all">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              </button>
              <button @click="viewMode = 'list'" :class="viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400'" class="p-1.5 rounded-lg transition-all">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
            </div>
            
            <button 
              v-if="selectedImages.length > 0"
              @click="deleteSelected"
              class="flex items-center gap-2 bg-rose-50 text-rose-600 px-3.5 py-2 rounded-xl text-xs font-bold border border-rose-200 hover:bg-rose-100 transition-all"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Supprimer ({{ selectedImages.length }})
            </button>
          </div>
        </div>

        <!-- Grille ou Liste des Médias -->
        <div class="flex-1 overflow-y-auto p-6 lg:p-8">
          
          <div v-if="loading" class="grid  md:grid-cols-4 xl:grid-cols-6 gap-4 animate-pulse">
            <div v-for="n in 12" :key="n" class="aspect-square bg-slate-200 rounded-2xl"></div>
          </div>

          <div v-else-if="media.length > 0" :class="viewMode === 'grid' ? 'grid  md:grid-cols-4 xl:grid-cols-6 gap-4' : 'space-y-2'">
            <div
              v-for="item in media"
              :key="item._id"
              @click="toggleSelect(item._id, $event)"
              :class="[
                'group relative bg-white border transition-all duration-200 cursor-pointer overflow-hidden',
                viewMode === 'grid' ? 'rounded-2xl aspect-square flex flex-col' : 'rounded-xl flex items-center p-3 gap-4',
                selectedImages.includes(item._id) ? 'ring-2 ring-blue-500 border-transparent shadow-sm' : 'border-slate-200 hover:border-blue-300 shadow-sm'
              ]"
            >
              <div :class="viewMode === 'grid' ? 'flex-1' : 'w-12 h-12 rounded-lg shrink-0'" class="relative overflow-hidden bg-slate-100">
                <img :src="item.url" :alt="item.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                
                <div v-if="selectedImages.includes(item._id)" class="absolute inset-0 bg-blue-600/20 backdrop-blur-[2px] flex items-center justify-center">
                  <div class="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-md">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                  </div>
                </div>

                <div class="absolute top-2 right-2 flex flex-col gap-1.5 translate-x-8 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all">
                   <button @click.stop="copyUrl(item.url)" class="p-1.5 bg-white/90 backdrop-blur shadow-sm rounded-lg text-slate-600 hover:text-blue-600">
                     <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                   </button>
                   <button @click.stop="editMedia(item)" class="p-1.5 bg-white/90 backdrop-blur shadow-sm rounded-lg text-slate-600 hover:text-amber-600">
                     <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                   </button>
                </div>
              </div>

              <div :class="viewMode === 'grid' ? 'p-3 pt-2' : 'flex-1'">
                <p class="text-xs font-bold text-slate-900 truncate">{{ item.name }}</p>
                <div class="flex items-center justify-between mt-0.5">
                  <p class="text-[10px] text-slate-400 font-medium tabular-nums">
                    {{ item.metadata?.width || 0 }}×{{ item.metadata?.height || 0 }} • {{ item.readableSize }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="h-full flex flex-col items-center justify-center p-12 text-center">
            <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4 border border-slate-200">
              <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <h3 class="text-base font-bold text-slate-800 mb-1">Aucun fichier trouvé</h3>
            <p class="text-slate-400 text-xs mb-6">Commencez par importer vos visuels publicitaires ou images d'écrans.</p>
            <button @click="showUploadModal = true" class="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm hover:bg-blue-700 transition-all">
              Importer des Fichiers
            </button>
          </div>
        </div>

        <!-- Pagination -->
        <footer v-if="pagination.pages > 1" class="px-6 py-3 bg-white border-t border-slate-200 flex items-center justify-between text-xs">
            <span class="font-semibold text-slate-500">
              Fichiers {{ (pagination.page - 1) * pagination.limit + 1 }} - {{ Math.min(pagination.page * pagination.limit, totalCount) }} sur {{ totalCount }}
            </span>
            <div class="flex gap-2">
              <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1" class="px-3.5 py-1.5 rounded-xl border border-slate-200 font-bold disabled:opacity-30 hover:bg-slate-50 transition-all">Précédent</button>
              <button @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.pages" class="px-3.5 py-1.5 rounded-xl bg-blue-600 text-white font-bold disabled:opacity-30 hover:bg-blue-700 transition-all">Suivant</button>
            </div>
        </footer>
      </main>
    </div>

    <!-- MODALE D'UPLOAD -->
    <Transition name="fade">
      <div v-if="showUploadModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 border border-slate-200">
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-bold text-slate-900">Importer des Médias</h3>
            <button @click="showUploadModal = false" class="text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div
            class="group relative border-2 border-dashed border-slate-200 rounded-xl p-8 text-center transition-all cursor-pointer hover:border-blue-400 hover:bg-blue-50/30"
            :class="{ 'border-blue-500 bg-blue-50/50': isDragging }"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragleave="isDragging = false"
            @click="$refs.fileInput.click()"
          >
            <input ref="fileInput" type="file" accept="image/*" multiple @change="handleFileSelect" class="hidden" />
            <div class="w-14 h-14 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-sm">
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            </div>
            <p class="text-sm font-bold text-slate-800 mb-1">Glissez-déposez vos fichiers ici</p>
            <p class="text-slate-400 text-[11px] font-semibold uppercase">JPG, PNG, WEBP, SVG</p>
          </div>

          <div class="mt-6 space-y-4">
            <div class="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
              <div class="flex-1">
                <label class="block text-[10px] font-bold text-slate-400 uppercase">Dossier cible</label>
                <input v-model="uploadFolder" type="text" class="w-full bg-transparent border-none p-0 focus:ring-0 text-xs font-bold text-slate-800" placeholder="root" />
              </div>
            </div>

            <div v-if="uploading" class="space-y-1.5">
              <div class="flex justify-between text-xs font-bold text-slate-600">
                <span>Envoi des fichiers...</span>
                <span>{{ uploadProgress }}%</span>
              </div>
              <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div class="h-full bg-blue-600 transition-all duration-300" :style="{ width: uploadProgress + '%' }"></div>
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button @click="showUploadModal = false" class="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50">Annuler</button>
            <button @click="$refs.fileInput.click()" class="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 shadow-sm">Sélectionner</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- MODALE D'ÉDITION -->
    <Transition name="fade">
      <div v-if="editingMedia" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-200">
          <div class="flex items-center gap-4 mb-6 pb-4 border-b border-slate-100">
            <div class="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
              <img :src="editingMedia.url" class="w-full h-full object-cover" />
            </div>
            <div class="overflow-hidden">
              <h3 class="text-base font-bold text-slate-900">Propriétés du média</h3>
              <p class="text-xs text-slate-400 truncate">{{ editingMedia.name }}</p>
            </div>
          </div>

          <div class="space-y-4 text-xs">
            <div>
              <label class="block font-bold text-slate-500 mb-1">Nom d'affichage</label>
              <input v-model="editForm.name" type="text" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block font-bold text-slate-500 mb-1">Dossier</label>
                <input v-model="editForm.folder" type="text" class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label class="block font-bold text-slate-500 mb-1">Tags</label>
                <input v-model="editForm.tags" type="text" placeholder="logo, pub..." class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-blue-500" />
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-between items-center pt-4 border-t border-slate-100">
            <button @click="deleteMedia(editingMedia._id)" class="text-xs font-bold text-rose-600 hover:underline">Supprimer</button>
            <div class="flex gap-2">
              <button @click="editingMedia = null" class="px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold hover:bg-slate-50">Annuler</button>
              <button @click="saveEdit" class="px-4 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 shadow-sm">Enregistrer</button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

const API_BASE = 'https://api.datafuse.fr/api/super-admin/media';

const loading = ref(false);
const media = ref([]);
const folders = ref([]);
const selectedFolder = ref('all');
const searchQuery = ref('');
const viewMode = ref('grid');
const selectedImages = ref([]);
const totalCount = ref(0);
const pagination = ref({ page: 1, pages: 1, total: 0, limit: 24 });

// Upload
const showUploadModal = ref(false);
const uploadFolder = ref('root');
const uploading = ref(false);
const uploadProgress = ref(0);
const isDragging = ref(false);
const fileInput = ref(null);

// Edit
const editingMedia = ref(null);
const editForm = ref({ name: '', description: '', folder: '', tags: '' });

const fetchMedia = async () => {
  loading.value = true;
  try {
    const token = localStorage.getItem('superAdminToken');
    const params = new URLSearchParams({
      page: pagination.value.page,
      limit: pagination.value.limit,
      folder: selectedFolder.value,
      ...(searchQuery.value && { search: searchQuery.value })
    });

    const res = await fetch(`${API_BASE}/list?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (res.ok) {
      const data = await res.json();
      media.value = data.media;
      pagination.value = data.pagination;
      totalCount.value = data.pagination.total;
    }
  } catch (err) {
    toast.error('Échec du chargement des fichiers');
  } finally {
    loading.value = false;
  }
};

const fetchFolders = async () => {
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/folders`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      folders.value = data.folders;
    }
  } catch (err) {
    console.error(err);
  }
};

const selectFolder = (folder) => {
  selectedFolder.value = folder;
  pagination.value.page = 1;
  fetchMedia();
};

let searchTimeout;
const debounceSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    pagination.value.page = 1;
    fetchMedia();
  }, 400);
};

const toggleSelect = (id, event) => {
  if (event.ctrlKey || event.metaKey) {
    const index = selectedImages.value.indexOf(id);
    index > -1 ? selectedImages.value.splice(index, 1) : selectedImages.value.push(id);
  } else {
    selectedImages.value = [id];
  }
};

const handleFileSelect = (e) => uploadFiles(e.target.files);
const handleDrop = (e) => {
  isDragging.value = false;
  uploadFiles(e.dataTransfer.files);
};

const uploadFiles = async (files) => {
  if (!files.length) return;
  uploading.value = true;
  uploadProgress.value = 0;

  try {
    const token = localStorage.getItem('superAdminToken');
    const formData = new FormData();
    formData.append('folder', uploadFolder.value || 'root');
    for (let file of files) { formData.append('images', file); }

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) uploadProgress.value = Math.round((e.loaded / e.total) * 100);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 201 || xhr.status === 200) {
        toast.success('Importation réussie');
        showUploadModal.value = false;
        fetchMedia();
        fetchFolders();
      } else {
        toast.error('Erreur lors de l\'upload');
      }
      uploading.value = false;
    });

    xhr.open('POST', `${API_BASE}/upload-batch`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  } catch (err) {
    toast.error('Erreur réseau');
    uploading.value = false;
  }
};

const editMedia = (item) => {
  editingMedia.value = item;
  editForm.value = {
    name: item.name,
    description: item.description || '',
    folder: item.folder,
    tags: item.tags?.join(', ') || ''
  };
};

const saveEdit = async () => {
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/${editingMedia.value._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(editForm.value)
    });

    if (res.ok) {
      toast.success('Média mis à jour');
      editingMedia.value = null;
      fetchMedia();
      fetchFolders();
    }
  } catch (err) {
    toast.error('Erreur de sauvegarde');
  }
};

const deleteMedia = async (id) => {
  if (!confirm('Voulez-vous supprimer cet asset ?')) return;
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      toast.success('Média supprimé');
      editingMedia.value = null;
      fetchMedia();
      fetchFolders();
    }
  } catch (err) {
    toast.error('Suppression impossible');
  }
};

const deleteSelected = async () => {
  if (!confirm(`Supprimer ces ${selectedImages.value.length} fichiers ?`)) return;
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/delete-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ ids: selectedImages.value })
    });
    if (res.ok) {
      toast.success('Assets supprimés');
      selectedImages.value = [];
      fetchMedia();
      fetchFolders();
    }
  } catch (err) {
    toast.error('Erreur de suppression par lot');
  }
};

const copyUrl = (url) => {
  navigator.clipboard.writeText(url);
  toast.success('Lien copié dans le presse-papier', { autoClose: 1500 });
};

const changePage = (page) => {
  pagination.value.page = page;
  fetchMedia();
};

onMounted(() => {
  fetchMedia();
  fetchFolders();
});
</script>